import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlaidLink } from 'react-plaid-link';
import {
  ArrowLeftIcon,
  LinkIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Sankey,
  Layer,
  Rectangle,
  Text,
} from 'recharts';
import axios from 'axios';
import HeaderNav from './HeaderNav';
import AuraCashflowChat from './cashflow/AuraCashflowChat';
import CashflowAlluvialChart from './cashflow/CashflowAlluvialChart';
import CashflowEntryForm from './cashflow/CashflowEntryForm';
import SankeyExplainer from './cashflow/SankeyExplainer';
import SankeyFlowTooltip from './cashflow/SankeyFlowTooltip';
import ProgressPanel from './common/ProgressPanel';
import { CHART_RAMP, CHART_TOOLTIP_STYLE, SANKEY_LINK, SANKEY_NODE } from '../design/tokens';

const sankeyIndex = (nodes, id) => nodes.findIndex((n) => n.id === id);

const PAY_CYCLE_HINT = (key) => {
  const map = {
    every_friday: 'Every Friday',
    every_other_friday: 'Every other Friday',
    '1st_and_15th': '1st & 15th',
    monthly_1st: 'Monthly (1st)',
    monthly_15th: 'Monthly (15th)',
  };
  return map[key] || key.replace(/_/g, ' ');
};

const SankeyNode = ({ x, y, width, height, index, payload }) => {
  if (width <= 0 || height <= 0) return null;
  return (
    <Layer key={`node-${index}`}>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={SANKEY_NODE.fill}
        stroke={SANKEY_NODE.stroke}
        strokeWidth={1}
      />
      <Text
        x={x < 10 ? x + width + 6 : x - 6}
        y={y + height / 2}
        textAnchor={x < 10 ? 'start' : 'end'}
        verticalAnchor="middle"
        fill="#211D16"
        fontSize={11}
        fontFamily="Fraunces Variable, Georgia, serif"
      >
        {payload.name}
      </Text>
    </Layer>
  );
};

const CashflowPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkToken, setLinkToken] = useState(null);
  const [plaidLoading, setPlaidLoading] = useState(false);
  const [entryDrawerOpen, setEntryDrawerOpen] = useState(false);
  const [flowView, setFlowView] = useState('sankey');
  const [chatSeed, setChatSeed] = useState(null);
  const [proposalsRefreshKey, setProposalsRefreshKey] = useState(0);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/cashflow/summary', { timeout: 20000 });
      setSummary(res.data);
    } catch (e) {
      console.error('Cashflow summary failed:', e);
      setSummary({ has_data: false, has_income: false, has_expenses: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const hasData = summary?.has_data === true;

  const onPlaidSuccess = useCallback(
    async (publicToken) => {
      setPlaidLoading(true);
      try {
        await axios.post('/api/plaid/exchange_cashflow_token', {
          public_token: publicToken,
          user_id: user?.user_id,
        });
        await loadSummary();
        setEntryDrawerOpen(false);
      } catch (e) {
        console.error(e);
      } finally {
        setPlaidLoading(false);
        setLinkToken(null);
      }
    },
    [user?.user_id, loadSummary]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: () => {
      setPlaidLoading(false);
      setLinkToken(null);
    },
  });

  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  const connectBank = async () => {
    setPlaidLoading(true);
    try {
      const res = await axios.post('/api/plaid/create_cashflow_link_token');
      setLinkToken(res.data.link_token);
    } catch (e) {
      console.error(e);
      setPlaidLoading(false);
    }
  };

  const handleEntrySaved = async () => {
    setEntryDrawerOpen(false);
    setLoading(true);
    await loadSummary();
  };

  const flow = summary?.flow;
  const breakdown = summary?.breakdown;
  const kpis = summary?.kpis || {};
  const visionImpact = summary?.vision_impact || {};

  const sankeyData = React.useMemo(() => {
    if (!flow?.nodes?.length || !flow?.links?.length) return null;
    const nodes = flow.nodes.map((n) => ({ name: n.label }));
    const links = flow.links
      .map((l) => ({
        source: sankeyIndex(flow.nodes, l.source),
        target: sankeyIndex(flow.nodes, l.target),
        value: Math.max(l.value, 0.01),
      }))
      .filter((l) => l.source >= 0 && l.target >= 0);
    if (!links.length) return null;
    return { nodes, links };
  }, [flow]);

  const pieData = (breakdown?.categories || [])
    .filter((c) => c.amount > 0)
    .slice(0, 8)
    .map((c) => ({ name: c.name, value: c.amount, type: c.type }));

  const pieTotal = pieData.reduce((s, d) => s + d.value, 0);

  const spendMixLegend = (
    <ul className="hairline-list text-body text-ink">
      {pieData.map((d, i) => {
        const pct = pieTotal > 0 ? (d.value / pieTotal) * 100 : 0;
        return (
          <li key={d.name} className="flex items-center justify-between gap-3 py-2.5">
            <span className="flex items-center gap-2 min-w-0">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: CHART_RAMP[i % CHART_RAMP.length] }}
              />
              <span className="truncate">{d.name}</span>
            </span>
            <span className="figure text-ink shrink-0">
              {pct.toFixed(0)}%
            </span>
          </li>
        );
      })}
    </ul>
  );

  const entryRowsFromSummary = React.useMemo(() => {
    const cats = breakdown?.categories || [];
    if (!cats.length) return null;
    return cats.map((c, i) => ({
      id: `cat-${i}`,
      label: c.name,
      amount: String(c.amount),
      essential: c.type === 'essential',
    }));
  }, [breakdown?.categories]);

  const showEntryOnly = !loading && !hasData;
  const showInsights = !loading && hasData;

  const kpiItems = [
    { label: 'Safe to spend', value: kpis.safe_to_spend, fmt: '$', suffix: '' },
    { label: 'Investable surplus', value: kpis.investable_surplus, fmt: '$', suffix: '' },
    {
      label: 'Pay cycle',
      value: kpis.income_pulse_pct,
      fmt: '',
      suffix: '%',
      hint:
        kpis.pay_cycle_label
          ? PAY_CYCLE_HINT(kpis.pay_cycle_label)
          : summary?.income_pattern?.next_pay_date
            ? `Next: ${String(summary.income_pattern.next_pay_date).slice(0, 10)}`
            : null,
    },
  ];

  return (
    <div className="min-h-screen bg-bone">
      <HeaderNav
        activeSection="cashflow"
        onSectionChange={(section) => {
          if (section === 'dashboard') navigate('/dashboard');
          else if (section === 'visions') navigate('/visions');
          else if (section === 'community') navigate('/community');
        }}
        user={user}
        onLogout={onLogout}
        visionCount={0}
      />

      <div className="pt-24 pb-12 px-6 md:px-8 max-w-[1600px] mx-auto">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-ink-muted hover:text-ink mb-6 text-body transition-colors duration-300"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to dashboard
        </button>

        <header className="flex flex-wrap items-end justify-between gap-6 mb-8 pb-8 border-b border-hairline">
          <div>
            <h1 className="font-display text-h1 text-ink">Cashflow</h1>
            <p className="text-body text-ink-muted mt-2">
              {showEntryOnly
                ? 'Start with a quick picture of income and spending'
                : summary?.data_source
                  ? `Data: ${summary.data_source}`
                  : 'Your money flow and goals'}
            </p>
          </div>
          <div className="flex gap-2">
            {!summary?.has_plaid && (
              <button
                type="button"
                onClick={connectBank}
                disabled={plaidLoading}
                className="btn-secondary text-label"
              >
                <LinkIcon className="w-4 h-4 mr-1.5 inline" />
                {plaidLoading ? 'Connecting…' : 'Connect bank'}
              </button>
            )}
            {showInsights && (
              <button
                type="button"
                onClick={() => setEntryDrawerOpen(true)}
                className="btn-secondary text-label"
              >
                <PencilSquareIcon className="w-4 h-4 mr-1.5 inline" />
                Add / edit expenses
              </button>
            )}
          </div>
        </header>

        {loading && (
          <p className="text-body text-ink-muted py-12 text-center">Loading…</p>
        )}

        {showEntryOnly && (
          <CashflowEntryForm
            initialIncome={summary?.income_pattern}
            tagOverrides={summary?.tag_overrides || {}}
            onSaved={handleEntrySaved}
            onConnectBank={connectBank}
            plaidLoading={plaidLoading}
          />
        )}

        {showInsights && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-start">
            <div
              id="cashflow-ask-aura"
              className="order-1 lg:order-2 flex flex-col min-h-[min(560px,calc(100vh-7rem))] lg:min-h-[calc(100vh-7rem)] lg:h-[calc(100vh-7rem)]"
            >
              <div className="surface-emphasis border-t border-brass flex flex-col flex-1 min-h-0 p-5 md:p-6">
                <AuraCashflowChat
                  user={user}
                  summary={summary}
                  chatSeed={chatSeed}
                  onChatSeedConsumed={() => setChatSeed(null)}
                  onDataChanged={loadSummary}
                  proposalsRefreshKey={proposalsRefreshKey}
                />
              </div>
            </div>

            <div className="order-2 lg:order-1 flex flex-col gap-8">
              {summary?.progress && (
                <section className="pb-6 border-b border-hairline">
                  <ProgressPanel progress={summary.progress} compact />
                </section>
              )}

              <section>
                <h2 className="font-display text-h2 text-ink mb-4">At a glance</h2>
                <ul className="hairline-list">
                  {kpiItems.map((kpi) => (
                    <li
                      key={kpi.label}
                      className="flex items-baseline justify-between gap-4 py-4 first:pt-0"
                    >
                      <span className="text-label text-ink-muted">{kpi.label}</span>
                      <div className="text-right">
                        <span className="figure text-display text-ink">
                          {kpi.fmt}
                          {(kpi.value ?? 0).toLocaleString('en-US', {
                            maximumFractionDigits: 0,
                          })}
                          {kpi.suffix || ''}
                        </span>
                        {kpi.hint && (
                          <p className="text-label text-ink-muted mt-1 truncate max-w-[200px]" title={kpi.hint}>
                            {kpi.hint}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>

              {visionImpact.primary_vision_title && (
                <section className="py-6 border-t border-hairline">
                  <p className="text-label text-ink-muted mb-1">Vision impact</p>
                  <h3 className="font-display text-h3 text-ink mb-2">
                    {visionImpact.primary_vision_title}
                  </h3>
                  <p className="text-body text-ink-muted">
                    {visionImpact.message ||
                      `Redirecting lifestyle spend could reach your goal ${visionImpact.days_sooner || 0} days sooner.`}
                  </p>
                  <span
                    className={`inline-block mt-3 text-label px-2 py-0.5 rounded-full border ${
                      kpis.vision_status === 'at_risk'
                        ? 'border-warning text-warning'
                        : 'border-pine text-pine'
                    }`}
                  >
                    {kpis.vision_status === 'at_risk' ? 'At risk' : 'On track'}
                  </span>
                </section>
              )}

              <section className="pt-2">
                <SankeyExplainer flow={flow} kpis={kpis} />
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-h2 text-ink">Money flow</h2>
                  <div className="flex border border-hairline rounded-md overflow-hidden text-label">
                    <button
                      type="button"
                      onClick={() => setFlowView('sankey')}
                      className={`px-3 py-1.5 transition-colors duration-300 ${
                        flowView === 'sankey'
                          ? 'bg-pine-tint text-pine'
                          : 'text-ink-muted hover:bg-pine-tint'
                      }`}
                    >
                      Sankey
                    </button>
                    <button
                      type="button"
                      onClick={() => setFlowView('alluvial')}
                      className={`px-3 py-1.5 border-l border-hairline transition-colors duration-300 ${
                        flowView === 'alluvial'
                          ? 'bg-pine-tint text-pine'
                          : 'text-ink-muted hover:bg-pine-tint'
                      }`}
                    >
                      Alluvial
                    </button>
                  </div>
                </div>
                {flowView === 'sankey' ? (
                  sankeyData ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <Sankey
                        data={sankeyData}
                        margin={{ left: 72, right: 72, top: 8, bottom: 8 }}
                        nodePadding={28}
                        nodeWidth={10}
                        link={SANKEY_LINK}
                        node={<SankeyNode />}
                      >
                        <Tooltip content={<SankeyFlowTooltip />} />
                      </Sankey>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-body text-ink-muted py-8 text-center">
                      Add more detail via Add / edit expenses to refine your flow.
                    </p>
                  )
                ) : (
                  <CashflowAlluvialChart flow={flow} />
                )}
              </section>

              <section className="pt-6 border-t border-hairline">
                <h2 className="font-display text-h2 text-ink mb-4">Spend mix</h2>
                {pieData.length > 0 ? (
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="w-full md:w-[58%] h-[260px] md:h-[280px] shrink-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={52}
                            outerRadius={100}
                            label={false}
                            paddingAngle={1}
                            stroke="#E0D9CA"
                            strokeWidth={1}
                          >
                            {pieData.map((_, i) => (
                              <Cell key={i} fill={CHART_RAMP[i % CHART_RAMP.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(v) => [
                              `$${Number(v).toFixed(0)}`,
                              'Amount',
                            ]}
                            contentStyle={CHART_TOOLTIP_STYLE}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="w-full md:w-[42%] md:max-h-[280px] md:overflow-y-auto">
                      {spendMixLegend}
                    </div>
                  </div>
                ) : (
                  <p className="text-body text-ink-muted py-8 text-center">
                    Categorize spending in Add / edit expenses to see your mix.
                  </p>
                )}
              </section>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {entryDrawerOpen && showInsights && (
          <>
            <motion.div
              className="fixed inset-0 bg-ink/20 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              onClick={() => setEntryDrawerOpen(false)}
            />
            <motion.div
              className="fixed inset-y-0 right-0 z-50 w-full max-w-lg overflow-y-auto bg-bone border-l border-hairline p-6 md:p-8"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <div className="flex justify-between items-center mb-6 pb-4 border-b border-hairline">
                <h3 className="font-display text-h2 text-ink">Update your money picture</h3>
                <button
                  type="button"
                  onClick={() => setEntryDrawerOpen(false)}
                  className="p-1 text-ink-muted hover:text-ink"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <CashflowEntryForm
                embedded
                initialIncome={summary?.income_pattern}
                initialRows={entryRowsFromSummary}
                tagOverrides={summary?.tag_overrides || {}}
                onSaved={handleEntrySaved}
                onConnectBank={connectBank}
                plaidLoading={plaidLoading}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CashflowPage;
